/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { addDays, format, toDate } from 'date-fns';
import { StringHelper } from 'sarsaparilla';
import { Footer } from 'site-kit';
import TripItineraryArrival from './TripItineraryArrival';
import TripItineraryDeparture from './TripItineraryDeparture';
import TripItineraryRowHeader from './TripItineraryRowHeader';
import TripItineraryItems from './TripItineraryItems';
import * as types from '../constants';
import { scrollTo } from '../utils/uiUtils';
import { getTripDateRange } from '../utils/tripUtils';
import { isBetweenDays } from '../utils/dateUtils';

const propTypes = {
    trip: PropTypes.object,
    activeDate: PropTypes.string,
    activeDestinationItineraryIndex: PropTypes.number,
    onMoveUpDestination: PropTypes.func,
    onMoveDownDestination: PropTypes.func,
    onDeleteDestination: PropTypes.func,
    onBookTripItem: PropTypes.func,
    onDeleteTripItem: PropTypes.func,
    onDestinationSelected: PropTypes.func,
    onTripItemSelected: PropTypes.func,
    editable: PropTypes.bool,
};

const sluggify = StringHelper.sluggify;

export default class TripItinerary extends React.Component {
    static propTypes = propTypes;

    constructor(props) {
        super(props);
        this.state = {
            activeDate: props.activeDate,
            activeDestinationItineraryIndex: props.activeDestinationItineraryIndex,
        };
        this.headerRefs = {};

        this.onMoveUpDestination = this.onMoveUpDestination.bind(this);
        this.onMoveDownDestination = this.onMoveDownDestination.bind(this);
        this.onDeleteDestination = this.onDeleteDestination.bind(this);
        this.onBookTripItem = this.onBookTripItem.bind(this);
        this.onDeleteTripItem = this.onDeleteTripItem.bind(this);
        this.onTripItemSelected = this.onTripItemSelected.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.activeDate && nextProps.activeDate !== this.state.activeDate) {
            // scroll itinerary to specific date on change
            if (this._ref) {
                const day = format(nextProps.activeDate, 'MMM d yyyy');

                this.scrollToDay(day);
                this.setState({
                    activeDate: nextProps.activeDate,
                });
            }
        } else if (
            nextProps.activeDestinationItineraryIndex &&
            nextProps.activeDestinationItineraryIndex !==
                this.state.activeDestinationItineraryIndex &&
            nextProps.activeDate !== this.state.activeDate
        ) {
            // scroll itinerary to specific destination on change
            if (this._ref) {
                let index = this.getDestinationOffset(
                    nextProps.activeDestinationItineraryIndex
                );
                if (index < 0) {
                    // if not found, then scroll to bottom
                    index = this._ref.children.length - 1;
                }
                this.scrollToIndex(index);
                this.setState({
                    activeDestinationItineraryIndex:
                        nextProps.activeDestinationItineraryIndex,
                });
            }
        }
    }

    onMoveUpDestination(destinationIndex) {
        if (this.props.onMoveUpDestination) {
            this.props.onMoveUpDestination(destinationIndex);
        }
    }

    onMoveDownDestination(destinationIndex) {
        if (this.props.onMoveDownDestination) {
            this.props.onMoveDownDestination(destinationIndex);
        }
    }

    onDeleteDestination(destinationIndex) {
        if (this.props.onDeleteDestination) {
            this.props.onDeleteDestination(destinationIndex);
        }
    }

    onBookTripItem(destinationIndex, item) {
        if (this.props.onBookTripItem) {
            this.props.onBookTripItem(destinationIndex, item);
        }
    }

    onDeleteTripItem(destinationIndex, item) {
        if (this.props.onDeleteTripItem) {
            this.props.onDeleteTripItem(destinationIndex, item);
        }
    }

    onTripItemSelected(destinationIndex, itemIndex) {
        if (this.props.onTripItemSelected) {
            this.props.onTripItemSelected(destinationIndex, itemIndex);
        }
    }

    getDestinationOffset(destinationIndex) {
        const destination = this.props.trip.destinations[destinationIndex];

        const destinationMap = this.getDestinationsByDates();
        let counter = 0;
        for (const arrivalDepartures of destinationMap) {
            for (let index = 0; index < arrivalDepartures.length; index += 1) {
                const elements = arrivalDepartures[index];
                if (Array.isArray(elements)) {
                    for (let elindex = 0; elindex < elements.length; elindex += 1) {
                        const element = elements[elindex];

                        if (element.arrival === destination) {
                            return counter - 1;
                        }

                        if (element.departure) {
                            counter += 1;
                        } else if (element.arrival) {
                            counter += 1;

                            if (
                                element.arrival.items &&
                                element.arrival.items.length > 0
                            ) {
                                counter += element.arrival.items.length;
                            }
                        }
                    }
                } else {
                    counter += 1;
                }
            }
        }
        return 0;
    }

    // group trip destinations by trip dates
    getDestinationsByDates() {
        const tripDateRange = getTripDateRange(this.props.trip);

        const destinationsMap = new Map();
        tripDateRange.forEach((item) => {
            destinationsMap.set(format(item, types.FNS_LL_DATE_FORMAT), []);
        });

        let currentArrivalDate = tripDateRange[0];
        this.props.trip.destinations.forEach((destination, index) => {
            if (index === 0) {
                destinationsMap
                    .get(format(currentArrivalDate, types.FNS_LL_DATE_FORMAT))
                    .push({
                        departure: destination,
                        destinationIndex: index,
                    });
            } else {
                const destinationDuration = parseInt(destination.duration, 10) || 0;
                const arrivalDate = toDate(currentArrivalDate);

                const departureDate = addDays(
                    toDate(currentArrivalDate),
                    destinationDuration
                );
                destinationsMap
                    .get(format(currentArrivalDate, types.FNS_LL_DATE_FORMAT))
                    .push({
                        arrival: destination,
                        arrivalDate,
                        departureDate,
                        destinationIndex: index,
                    });

                currentArrivalDate = addDays(currentArrivalDate, destinationDuration);
                destinationsMap
                    .get(format(currentArrivalDate, types.FNS_LL_DATE_FORMAT))
                    .push({
                        departure: destination,
                        destinationIndex: index,
                    });
            }
        });

        return destinationsMap;
    }

    // group trip destination items by item reservation dates or destination dates
    getItemsByDatesMap() {
        const tripDateRange = getTripDateRange(this.props.trip);

        const itemsMap = new Map();
        tripDateRange.forEach((item) => {
            itemsMap.set(format(item, types.FNS_LL_DATE_FORMAT), {});
        });

        let currentArrivalDate = tripDateRange[0];
        this.props.trip.destinations.forEach((destination, destinationIndex) => {
            if (destinationIndex > 0) {
                const destinationDuration = parseInt(destination.duration, 10);
                const arrivalDate = toDate(currentArrivalDate);
                const departureDate = addDays(
                    toDate(currentArrivalDate),
                    destinationDuration
                );
                const items = itemsMap.get(format(arrivalDate, types.FNS_LL_DATE_FORMAT));

                if (!items.destination) {
                    items.destination = destination;
                    items.destinationIndex = destinationIndex;
                    items.arrivalDate = arrivalDate;
                    items.departureDate = departureDate;
                    items.items = [];
                }

                destination.items.forEach((item) => {
                    let added = false;
                    if (item.dates && item.dates.length > 0) {
                        item.dates.forEach((itemDate) => {
                            const dt = itemDate;

                            if (isBetweenDays(dt, arrivalDate, departureDate)) {
                                const dtItems = itemsMap.get(
                                    format(dt, types.FNS_LL_DATE_FORMAT)
                                );

                                if (!dtItems.destination) {
                                    dtItems.destination = destination;
                                    dtItems.destinationIndex = destinationIndex;
                                    dtItems.arrivalDate = arrivalDate;
                                    dtItems.departureDate = departureDate;
                                    dtItems.items = [];
                                }
                                if (dtItems.items.indexOf(item) < 0) {
                                    dtItems.items.push(item);
                                }
                                added = true;
                            }
                        });
                    }
                    if (!added) {
                        const dtItems = itemsMap.get(
                            format(arrivalDate, types.FNS_LL_DATE_FORMAT)
                        );
                        if (dtItems.items.indexOf(item) < 0) {
                            dtItems.items.push(item);
                        }
                    }
                });

                currentArrivalDate = addDays(currentArrivalDate, destinationDuration);
            }
        });

        return itemsMap;
    }

    scrollToIndex(index) {
        const to = this._ref.children[index].offsetTop - this._ref.offsetTop - 10;
        scrollTo(this._ref, to, 100);
    }

    scrollToDay(day) {
        const dayId = sluggify(day);
        if (this.headerRefs[dayId]) {
            const to = this.headerRefs[dayId].offsetTop - this._ref.offsetTop - 10;
            scrollTo(this._ref, to, 100);
        }
    }

    renderItinerary() {
        const rows = [];
        const destinations = this.props.trip.destinations;

        if (!destinations || destinations.length < 1) {
            return rows;
        }

        const destinationMap = this.getDestinationsByDates();
        const itemsMap = this.getItemsByDatesMap();

        let arrivalIndex = 1;
        let activeDateStr = '';
        if (this.props.activeDate) {
            activeDateStr = format(this.props.activeDate, types.FNS_LL_DATE_FORMAT);
        }
        let counter = 0;

        // render destinations and trip items grouped by dates
        for (const [day, arrivalDepartures] of destinationMap) {
            // render date header
            let isActive = false;
            if (activeDateStr === day) {
                isActive = true;
            }

            const dayId = sluggify(day);

            rows.push(
                <TripItineraryRowHeader
                    isActive={isActive}
                    key={day}
                    innerRef={(node) => {
                        this.headerRefs[dayId] = node;
                    }}
                    title={day}
                />
            );

            // render arrival/departure
            for (let index = 0; index < arrivalDepartures.length; index += 1) {
                const element = arrivalDepartures[index];
                if (element.departure) {
                    const destinationIndex = arrivalIndex - 1;
                    if (destinationIndex < destinations.length - 1) {
                        rows.push(
                            <TripItineraryDeparture
                                key={`departure-${counter}-${index}`}
                                destinationIndex={destinationIndex}
                                destination={element.departure}
                            />
                        );
                    }
                } else if (element.arrival) {
                    rows.push(
                        <TripItineraryArrival
                            key={`arrival-${counter}-${index}`}
                            destinationIndex={arrivalIndex}
                            destination={element.arrival}
                            onMoveUpDestination={this.onMoveUpDestination}
                            onMoveDownDestination={this.onMoveDownDestination}
                            onDeleteDestination={this.onDeleteDestination}
                            onSelected={this.props.onDestinationSelected}
                            editable={this.props.editable}
                        />
                    );
                    arrivalIndex += 1;
                }

                counter += 1;
            }

            // render items for the date
            const dateItems = itemsMap.get(day);
            if (dateItems) {
                rows.push(
                    <TripItineraryItems
                        key={`item-${counter}`}
                        trip={this.props.trip}
                        items={dateItems.items}
                        destinationIndex={dateItems.destinationIndex}
                        destination={dateItems.destination}
                        onTripItemSelected={this.onTripItemSelected}
                        onBookTripItem={this.onBookTripItem}
                        onDeleteTripItem={this.onDeleteTripItem}
                        editable={this.props.editable}
                        arrivalDate={dateItems.arrivalDate}
                        departureDate={dateItems.departureDate}
                        tripId={this.props.trip.trip_id}
                    />
                );
            }

            counter += 1;
        }

        return rows;
    }

    render() {
        return (
            <div
                ref={(ref) => {
                    this._ref = ref;
                }}
                className="trip-planner-itinerary-wrap"
                data-testid="trip-planner-itinerary"
            >
                {this.renderItinerary()}
                <div className="results-footer-wrap">
                    <Footer inline />
                </div>
            </div>
        );
    }
}
